import React, { useState } from 'react'
import { CSVLink } from 'react-csv'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import {
  Box,
  Button,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material/'
import PropTypes from 'prop-types'

import AdminAPI from '../../../api/orbitV1/AdminAPI'
import DownloadIcon from '../../../assets/icons/circles-design/download_icon.png'
import PlusIcon from '../../../assets/icons/circles-design/plus.png'

/** Helper function to parse CSV file. */
function parseCsv(csvFile) {
  const csvFileRows = csvFile.split('\n')
  const values = []
  for (const dataRow of csvFileRows) {
    const newArr = dataRow.replaceAll('"', '').trim()
    values.push(newArr.split(','))
  }
  return values
}

/** Styled div that bolds content. */
const StrongText = styled.div({
  display: 'block',
  fontWeight: 'bold',
  fontSize: 16
})

/** Dotted box used for uploads */
function DottedBox(props) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed #B8C4D3',
        borderRadius: '8px',
        width: '20%',
        padding: '30px 35px 30px 30px'
      }}
    >
      {props.children}
    </Box>
  )
}

const stepIconSx = {
  '.MuiStepIcon-root.Mui-active': {
    fill: 'blue',
    fontWeight: 'bold'
  }
}

/**
 * Main component in this file.
 * @param {Object} props
 * @param {'iccid' | 'msisdn'} props.itemType
 */
function UploadViewTemplate(props) {
  const { itemType } = props

  const [uploadedFilename, setUploadedFilename] = useState(null)
  const [uploadReqBody, setUploadReqBody] = useState(null)

  const onFileUpload = (file) => {
    const fileName = file.name
    const reader = new FileReader()

    reader.onload = async (e) => {
      const text = e.target.result
      const csvArray = parseCsv(text)
      const requestBody = {}
      requestBody[`${itemType}s`] = csvArray
      setUploadReqBody(requestBody)
    }

    reader.readAsText(file)
    setUploadedFilename(fileName)
  }

  const onConfirmUpload = async () => {
    let API
    switch (itemType) {
      case 'iccid':
        API = AdminAPI.uploadIccid
        break
      case 'msisdn':
        API = AdminAPI.uploadMsisdn
        break
      default:
        break
    }

    try {
      const uploadResponse = await API(uploadReqBody)

      if (uploadResponse) toast.success('Upload success')
    } catch (error) {
      toast.error('Upload failed')
    }
  }

  const CSV_TEMPLATE_NAME = `${itemType}-template.csv`
  const CSV_HEADERS = [
    { label: `${itemType.toUpperCase()}`, key: itemType.toUpperCase() }
  ]
  const CSV_ICCID_FILEDATA = [
    { ICCID: '896572945007008368' },
    { ICCID: '896572940387491679' }
  ]
  const CSV_MSISDN_FILEDATA = [{ MSISDN: '87651234' }, { MSISDN: '89123456' }]
  const TemplateBox = () => (
    <CSVLink
      data={itemType === 'iccid' ? CSV_ICCID_FILEDATA : CSV_MSISDN_FILEDATA}
      filename={CSV_TEMPLATE_NAME}
      headers={CSV_HEADERS}
      style={{ textDecoration: 'none', cursor: 'pointer', display: 'flex' }}
    >
      <Typography
        sx={{
          color: '#0000FF',
          fontSize: '16px',
          fontWeight: 'bold',
          marginRight: '2px'
        }}
      >
        Download Template
      </Typography>
      <img
        src={DownloadIcon}
        alt='download-template-logo'
        width='20px'
        height='20px'
      />
    </CSVLink>
  )

  const UploadBoxEmpty = () => (
    <>
      <DottedBox>
        <>
          <label htmlFor={`upload-${itemType}`} style={{ cursor: 'pointer' }}>
            <Box display='flex' alignItems='center' justifyContent='center'>
              <Typography
                sx={{
                  color: '#0000FF',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginRight: '4px'
                }}
              >
                Select File
              </Typography>
              <img
                src={PlusIcon}
                alt='upload-csv-logo'
                width='20px'
                height='20px'
              />
            </Box>
          </label>
          <input
            id={`upload-${itemType}`}
            type='file'
            accept='.csv'
            onChange={(e) => onFileUpload(e.target.files[0])}
            hidden
          />
        </>
      </DottedBox>
      <Typography variant='subtitle1' sx={{ color: '#B8C4D3' }}>
        xlsx / csv, max 8mb
      </Typography>
      {uploadedFilename && (
        <Typography>
          Uploaded file: <strong>{uploadedFilename}</strong>
        </Typography>
      )}
    </>
  )

  return (
    <Stepper orientation='vertical' sx={stepIconSx}>
      <Step active>
        <StepLabel>
          <StrongText>
            Download and follow this template to upload a list of new{' '}
            {itemType.toUpperCase()}s.
          </StrongText>
        </StepLabel>
        <StepContent>
          <TemplateBox />
        </StepContent>
      </Step>
      <Step active>
        <StepLabel>
          <StrongText>
            List the new {itemType.toUpperCase()}s in Column 1 under the Header
            label &apos;{itemType.toUpperCase()}&apos;.
            <br />
            Save and upload the file below.
          </StrongText>
        </StepLabel>
        <StepContent>
          <UploadBoxEmpty />
        </StepContent>
      </Step>
      <Step active={Boolean(uploadedFilename)}>
        <StepLabel>
          <StrongText>Upload File</StrongText>
        </StepLabel>
        <StepContent>
          <Button variant='filled' onClick={onConfirmUpload}>
            Confirm upload file: <br />
            {uploadedFilename}
          </Button>
        </StepContent>
      </Step>
    </Stepper>
  )
}

DottedBox.propTypes = {
  children: PropTypes.element
}

UploadViewTemplate.propTypes = {
  itemType: PropTypes.string
}

export default UploadViewTemplate
